
import { Component, OnInit, inject, ViewChild, DestroyRef, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TournamentCourse, Course, Hole, Round, HoleYardage, TeeBox, Tournament, SourceTournament } from '../../../models';
import { FormsModule } from '@angular/forms';
import { TournamentService, CourseService } from '../../../services';
import { DEFAULT_TEEBOX_COLOR, DEFAULT_TEEBOX_NAME } from '../../../services/course/course.service';
import { LoggingService } from '../../../services/shared/logging/logging.service';
import { ToastrService } from 'ngx-toastr';
import { HoleInfoComponent } from '../../holes/hole-info/hole-info.component';
import { ConfirmationDialogComponent } from '../../shared/confirmation-dialog/confirmation-dialog.component';
import { AuditInfoComponent } from '../../shared/audit-info/audit-info.component';
import { forkJoin, of, Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { ImportModalComponent, ImportOptions } from '../../import-modal/import-modal.component';
import { ImportService } from '../../../services/import/import.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { resolveTournamentLpgaId } from '../../../utils/tournament.utils';

type CourseFormState = { name: string; shortName: string; lpgaId?: number };

@Component({
    selector: 'app-tournament-course-detail',
    standalone: true,
    imports: [FormsModule, HoleInfoComponent, ConfirmationDialogComponent, ImportModalComponent, AuditInfoComponent],
    templateUrl: './tournament-course-detail.component.html',
    styleUrl: './tournament-course-detail.component.scss',
})
export class TournamentCourseDetailComponent implements OnInit {
    private loggingService = inject(LoggingService);
    private importService = inject(ImportService);
    private destroyRef = inject(DestroyRef);

    private tournamentId!: string;
    private readonly NEW_COURSE_ID = '__new-course';

    @ViewChild(ConfirmationDialogComponent) confirmationDialog!: ConfirmationDialogComponent;
    @ViewChild(ImportModalComponent) importModal!: ImportModalComponent;

    isImporting = signal<boolean>(false);
    sourceCourseCount = signal<number>(0);
    currentTournament: Tournament | null = null;

    tournamentCourse: TournamentCourse = {} as TournamentCourse;
    tournamentCourses: TournamentCourse[] = []; // All courses for tabs
    course?: Course = undefined;
    rounds: Round[] = [];
    holes: Hole[] = [];
    holeYardages: HoleYardage[] = [];
    changedHoles: Hole[] = [];
    changedHoleYardages: HoleYardage[] = [];
    isCourseChanged = false;
    originalCourseForm: CourseFormState = { name: '', shortName: '', lpgaId: undefined };
    isNewCourse = false;
    isSaving = false;
    isRemoving = false;
    courseForm: CourseFormState = { name: '', shortName: '', lpgaId: undefined };
    teeBoxes: TeeBox[] = [];

    constructor(
        private readonly tournamentService: TournamentService,
        private readonly courseService: CourseService,
        private readonly route: ActivatedRoute,
        private readonly toastrService: ToastrService,
        private readonly router: Router
    ) { }

    ngOnInit(): void {
        // Get tournamentId from route params
        this.route.params.subscribe(params => {
            this.tournamentId = params['tournamentId'];

            // Load tournament details for import functionality
            this.loadTournamentDetails();

            // Check if courseId is provided in route
            const courseId = params['courseId'];

            if (courseId) {
                // Load specific course via resolver
                this.route.data.subscribe(({ tournamentCourse }) => {
                    this.tournamentCourse = tournamentCourse;
                    this.isNewCourse = false;
                    this.courseForm = {
                        name: tournamentCourse.courseName ?? '',
                        shortName: '',
                        lpgaId: undefined,
                    };
                    this.loadAllTournamentCourses();
                    this.loadCourseDetails();
                    this.loadTournamentRounds();
                });
            } else {
                // No courseId provided, load all courses and select first one
                this.loadTournamentRounds();
                this.loadAllTournamentCoursesAndSelectFirst();
            }
        });
    }

    /**
     * Load all courses for the tournament to display in tabs
     */
    private loadAllTournamentCourses(): void {
        if (!this.tournamentId) return;

        this.tournamentService.getTournamentCoursesByTournamentId(this.tournamentId).subscribe({
            next: (courses: TournamentCourse[]) => {
                this.tournamentCourses = courses;
            },
            error: (err: any) => {
                this.loggingService.error('Error loading tournament courses:', err);
            },
        });
    }

    /**
     * Load all courses for the tournament and automatically select the first one
     */
    private loadAllTournamentCoursesAndSelectFirst(): void {
        if (!this.tournamentId) return;

        this.tournamentService.getTournamentCoursesByTournamentId(this.tournamentId).subscribe({
            next: (courses: TournamentCourse[]) => {
                this.tournamentCourses = courses;

                if (courses.length > 0) {
                    // Select the first course
                    this.tournamentCourse = courses[0];
                    this.isNewCourse = false;
                    this.courseForm = {
                        name: this.tournamentCourse.courseName ?? '',
                        shortName: '',
                        lpgaId: undefined,
                    };
                    this.loadCourseDetails();
                } else {
                    // No courses found
                    this.loggingService.debug('No courses found for tournament:', this.tournamentId);
                }
            },
            error: (err: any) => {
                this.loggingService.error('Error loading tournament courses:', err);
                this.toastrService.error('Failed to load tournament courses', 'Error');
            },
        });
    }

    /**
     * Load full course details including holes and yardages
     */
    private loadCourseDetails(): void {
        if (!this.tournamentCourse.courseId || this.tournamentCourse.courseId === this.NEW_COURSE_ID) {
            return;
        }

        forkJoin({
            course: this.courseService.getById(this.tournamentCourse.courseId),
            holes: this.courseService.getHolesByCourse(this.tournamentCourse.courseId),
            holeYardages: this.courseService.getHoleYardagesByCourse(this.tournamentCourse.courseId),
            teeBoxes: this.courseService.getTeeBoxesByCourse(this.tournamentCourse.courseId),
        }).subscribe({
            next: ({ course, holes, holeYardages, teeBoxes }) => {
                this.course = course;
                this.holes = holes;
                this.holeYardages = holeYardages;
                this.teeBoxes = teeBoxes;
                this.courseForm = {
                    name: course.name ?? this.tournamentCourse.courseName ?? '',
                    shortName: course.shortName ?? '',
                    lpgaId: this.normalizeLpgaId(course.lpgaId),
                };
                this.originalCourseForm = { ...this.courseForm };
                this.isCourseChanged = false;
                this.tournamentCourse.courseName = this.courseForm.name;
                this.ensureHoleAndYardageCoverage();
            },
            error: (err: any) => {
                this.toastrService.error('Failed to load course details', 'Error');
                this.loggingService.error('Error loading course details:', err);
            },
        });
    }

    /**
     * Load tournament rounds for displaying in hole yardages
     */
    private loadTournamentRounds(): void {
        if (!this.tournamentId) return;

        this.tournamentService.getRounds(this.tournamentId).subscribe({
            next: (rounds: Round[]) => {
                this.rounds = rounds;
                if (this.isNewCourse) {
                    this.initializeHoleYardagesForNewCourse();
                }
                this.ensureHoleAndYardageCoverage();
            },
            error: (err: any) => {
                this.loggingService.error('Error loading tournament rounds:', err);
            },
        });
    }

    // Data loaded via resolver; no manual load() needed

    /**
     * Switch to a different course tab
     */
    selectCourseTab(courseId: string | undefined): void {
        if (!courseId) return;

        if (courseId === this.NEW_COURSE_ID) {
            if (!this.isNewCourse) {
                const placeholder = this.tournamentCourses.find((course) => course.courseId === this.NEW_COURSE_ID);
                if (placeholder) {
                    this.tournamentCourse = placeholder;
                    this.isNewCourse = true;
                    this.initializeNewCourse();
                }
            }
            return;
        }

        // Load the selected course without changing the route
        const selectedCourse = this.tournamentCourses.find(course => course.courseId === courseId);
        if (selectedCourse) {
            this.isNewCourse = false;
            this.isSaving = false;
            this.tournamentCourse = selectedCourse;
            this.courseForm = {
                name: selectedCourse.courseName ?? '',
                shortName: '',
                lpgaId: undefined,
            };
            this.originalCourseForm = { ...this.courseForm };
            this.isCourseChanged = false;
            this.loadCourseDetails();
        }
    }

    /**
     * Track hole changes
     */
    onHoleChanged(hole: Hole): void {
        // Update the main holes array so validation can see the changes
        const mainArrayIndex = this.holes.findIndex((h) => h.holeId === hole.holeId);
        if (mainArrayIndex !== -1) {
            this.holes[mainArrayIndex] = { ...this.holes[mainArrayIndex], ...hole };
        }

        // Track the change for saving
        const existing = this.changedHoles.find((h) => h.holeId === hole.holeId);
        if (existing) {
            Object.assign(existing, hole);
        } else {
            this.changedHoles.push(hole);
        }
    }

    /**
     * Track hole yardage changes
     */
    onHoleYardageChanged(holeYardage: HoleYardage): void {
        // Update the main holeYardages array so validation can see the changes
        const mainArrayIndex = this.holeYardages.findIndex((hy) => {
            if (hy.holeYardageId && holeYardage.holeYardageId) {
                return hy.holeYardageId === holeYardage.holeYardageId;
            }

            return (
                !hy.holeYardageId &&
                !holeYardage.holeYardageId &&
                hy.holeId === holeYardage.holeId &&
                hy.roundId === holeYardage.roundId
            );
        });
        if (mainArrayIndex !== -1) {
            this.holeYardages[mainArrayIndex] = { ...this.holeYardages[mainArrayIndex], ...holeYardage };
        }

        // Track the change for saving
        const existing = this.changedHoleYardages.find((hy) => {
            if (hy.holeYardageId && holeYardage.holeYardageId) {
                return hy.holeYardageId === holeYardage.holeYardageId;
            }

            return (
                !hy.holeYardageId &&
                !holeYardage.holeYardageId &&
                hy.holeId === holeYardage.holeId &&
                hy.roundId === holeYardage.roundId
            );
        });
        if (existing) {
            Object.assign(existing, holeYardage);
        } else {
            this.changedHoleYardages.push(holeYardage);
        }
    }

    importCourses(): void {
        if (!this.currentTournament?.importSourceId) {
            this.toastrService.error('No import source ID found for this tournament', 'Error');
            return;
        }

        // Create a SourceTournament object for the modal
        const sourceTournament: SourceTournament = {
            tournamentId: this.currentTournament.importSourceId,
            name: this.currentTournament.name || 'Tournament',
            coursesCount: 0,
            playersCount: 0,
        };

        // Open the import modal with courses-only context
        this.importModal.openModal(sourceTournament, this.isImporting);
    }

    onImportConfirmed(importOptions: ImportOptions): void {
        if (!this.currentTournament?.importSourceId) {
            this.toastrService.error('No import source ID found', 'Error');
            return;
        }

        this.isImporting.set(true);

        this.importService.importCourses(this.currentTournament.importSourceId)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (count: number) => {
                    this.toastrService.success(`Successfully imported ${count} course(s)!`, 'Success');
                    this.isImporting.set(false);
                    // Reload tournament courses
                    this.loadAllTournamentCoursesAndSelectFirst();
                },
                error: (error: any) => {
                    this.loggingService.error('Error importing courses:', error);
                    this.toastrService.error(
                        error?.error?.detail || 'Failed to import courses',
                        'Error'
                    );
                    this.isImporting.set(false);
                },
            });
    }

    private loadTournamentDetails(): void {
        if (!this.tournamentId) return;

        this.tournamentService.getById(this.tournamentId).subscribe({
            next: (tournament: Tournament) => {
                this.currentTournament = tournament;
                // Load source courses count for import button state
                this.loadSourceCoursesCount(tournament);
            },
            error: (err: any) => {
                this.loggingService.error('Error loading tournament details:', err);
            },
        });
    }

    private resolveCourseLpgaId(): number | undefined {
        const formValue = this.normalizeLpgaId(this.courseForm?.lpgaId);
        if (formValue) {
            return formValue;
        }

        const courseValue = this.normalizeLpgaId(this.course?.lpgaId);
        if (courseValue) {
            return courseValue;
        }

        return resolveTournamentLpgaId(this.currentTournament);
    }

    private loadSourceCoursesCount(tournament: Tournament): void {
        // Only attempt to load source courses if tournament was imported from an external source
        if (!tournament?.importSourceId || tournament.importSourceId <= 0) {
            this.sourceCourseCount.set(0);
            return;
        }

        const lpgaTournamentId = tournament.importSourceId.toString();

        this.importService
            .getCoursesBySourceTournamentIdEndpoint('LPGA', lpgaTournamentId)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (courses) => {
                    this.sourceCourseCount.set(courses?.length ?? 0);
                },
                error: (error) => {
                    this.loggingService.error('Error loading source courses count:', error);
                    this.sourceCourseCount.set(0);
                },
            });
    }

    addCourse(): void {
        if (this.isNewCourse) {
            this.toastrService.info('Finish creating the current course before adding another.', 'Info');
            return;
        }

        const placeholder: TournamentCourse = {
            tournamentId: this.tournamentId,
            courseId: this.NEW_COURSE_ID,
            courseName: 'New Course',
        } as TournamentCourse;

        if (!this.tournamentCourses.find((course) => course.courseId === this.NEW_COURSE_ID)) {
            this.tournamentCourses = [...this.tournamentCourses, placeholder];
        }

        this.tournamentCourse = placeholder;
        this.isNewCourse = true;
        this.course = undefined;
        this.courseForm = {
            name: '',
            shortName: '',
            lpgaId: resolveTournamentLpgaId(this.currentTournament),
        };
        this.teeBoxes = [];
        this.initializeNewCourse();
    }

    private initializeNewCourse(): void {
        this.holes = Array.from({ length: 18 }, (_, index) => ({
            holeId: `${this.NEW_COURSE_ID}-hole-${index + 1}`,
            courseId: this.NEW_COURSE_ID,
            number: index + 1,
            par: undefined,
            handicap: index + 1,
            isDriveMeasured: false,
        } as Hole));

        this.changedHoles = [];
        this.changedHoleYardages = [];
        this.initializeHoleYardagesForNewCourse();
    }

    private initializeHoleYardagesForNewCourse(): void {
        if (!this.isNewCourse) {
            return;
        }

        this.holeYardages = [];
        this.ensureHoleAndYardageCoverage();
    }

    onCourseNameChanged(value: string): void {
        this.courseForm.name = value ?? '';
        if (this.isNewCourse) {
            this.tournamentCourse.courseName = this.courseForm.name || 'New Course';
        } else {
            this.checkCourseChanges();
        }
    }

    onCourseShortNameChanged(value: string): void {
        const formatted = (value ?? '').toUpperCase().slice(0, 3);
        if (formatted !== this.courseForm.shortName) {
            this.courseForm.shortName = formatted;
        }
        if (!this.isNewCourse) {
            this.checkCourseChanges();
        }
    }

    onCourseLpgaIdChanged(value: string | number | null): void {
        const normalized = this.normalizeLpgaId(value);
        this.courseForm.lpgaId = normalized;
        if (!this.isNewCourse) {
            this.checkCourseChanges();
        }
    }

    /**
     * Check if course information has changed
     */
    private checkCourseChanges(): void {
        this.isCourseChanged =
            this.courseForm.name !== this.originalCourseForm.name ||
            this.courseForm.shortName !== this.originalCourseForm.shortName ||
            this.courseForm.lpgaId !== this.originalCourseForm.lpgaId;
    }

    /**
     * Remove tournament course (removes the course from the tournament, but not the course itself)
     */
    remove(): void {
        if (this.isRemoving || this.isNewCourse) {
            return;
        }

        const courseId = this.tournamentCourse.courseId;
        const courseName = this.tournamentCourse.courseName;

        if (!courseId) {
            this.toastrService.error('Course identifier is missing. Please reload the page.', 'Error');
            return;
        }

        this.confirmationDialog.open({
            title: 'Remove Course from Tournament',
            message: `Are you sure you want to remove "<strong>${courseName}</strong>" from this tournament?<br><br><em>Note: This will only remove the course from the tournament, not delete the course itself.</em>`,
            confirmButtonText: 'Remove Course',
            cancelButtonText: 'Cancel',
            dangerMode: true
        });
    }

    /**
     * Called when user confirms course removal
     */
    onRemoveConfirmed(): void {
        const courseId = this.tournamentCourse.courseId;

        if (!courseId) {
            return;
        }

        this.isRemoving = true;
        this.isSaving = true; // Disable both buttons during removal

        this.tournamentService.deleteTournamentCourse(this.tournamentId, courseId).subscribe({
            next: () => {
                this.toastrService.success('Course removed from tournament successfully', 'Success');

                // Remove the course from the local list
                this.tournamentCourses = this.tournamentCourses.filter(c => c.courseId !== courseId);

                // If there are remaining courses, select the first one
                if (this.tournamentCourses.length > 0) {
                    this.selectCourseTab(this.tournamentCourses[0].courseId);
                } else {
                    // No more courses, reset the component state
                    this.tournamentCourse = {} as TournamentCourse;
                    this.course = undefined;
                    this.holes = [];
                    this.holeYardages = [];
                    this.teeBoxes = [];
                }

                this.isRemoving = false;
                this.isSaving = false;
            },
            error: (err: any) => {
                this.toastrService.error('Failed to remove course from tournament', 'Error');
                this.loggingService.error('Error removing tournament course:', err);
                this.isRemoving = false;
                this.isSaving = false;
            },
        });
    }

    /**
     * Called when user cancels course removal
     */
    onRemoveCancelled(): void {
        // Nothing to do, dialog will close automatically
    }

    /**
     * Save all changes (holes and yardages)
     */
    save(): void {
        if (this.isSaving) {
            return;
        }

        if (this.isNewCourse) {
            this.saveNewCourse();
            return;
        }

        if (!this.isCourseChanged && this.changedHoles.length === 0 && this.changedHoleYardages.length === 0) {
            this.toastrService.info('No changes to save', 'Info');
            return;
        }

        const courseId = this.tournamentCourse.courseId;
        if (!courseId) {
            this.toastrService.error('Course identifier is missing. Please reload the page.', 'Error');
            return;
        }

        let lpgaIdForUpdate: number | undefined = undefined;
        let saveCourseOperation: Observable<Course | null> = of(null);
        if (this.isCourseChanged && this.course) {
            lpgaIdForUpdate = this.resolveCourseLpgaId();
            if (!lpgaIdForUpdate) {
                this.toastrService.error('LPGA ID is required to save this course.', 'Validation Error');
                return;
            }

            saveCourseOperation = this.courseService.updateCourse({
                ...this.course,
                name: this.courseForm.name,
                shortName: this.courseForm.shortName,
                lpgaId: lpgaIdForUpdate,
            });
        }

        const saveHoleOperations = this.changedHoles.map((hole) => this.courseService.saveHole(hole));
        const createYardageOperations = this.changedHoleYardages
            .filter((yardage) => this.shouldCreateHoleYardage(yardage))
            .map((yardage) => this.courseService.createHoleYardage(courseId, yardage.holeId!, yardage));
        const updateYardageOperations = this.changedHoleYardages
            .filter((yardage) => this.shouldUpdateHoleYardage(yardage))
            .map((yardage) => this.courseService.saveHoleYardage(yardage));

        if (!this.isCourseChanged && !saveHoleOperations.length && !createYardageOperations.length && !updateYardageOperations.length) {
            if (this.changedHoleYardages.length > 0) {
                this.toastrService.warning('Enter a positive yardage value before saving.', 'Validation');
            } else {
                this.toastrService.info('No changes to save', 'Info');
            }
            return;
        }

        this.isSaving = true;

        forkJoin({
            course: saveCourseOperation,
            holes: saveHoleOperations.length ? forkJoin(saveHoleOperations) : of([] as Hole[]),
            createdYardages: createYardageOperations.length ? forkJoin(createYardageOperations) : of([] as HoleYardage[]),
            savedYardages: updateYardageOperations.length ? forkJoin(updateYardageOperations) : of([] as HoleYardage[]),
        }).subscribe({
            next: ({ course, holes, createdYardages, savedYardages }) => {
                if (course) {
                    this.course = course;
                    if (lpgaIdForUpdate && (!this.course.lpgaId || this.course.lpgaId <= 0)) {
                        this.course.lpgaId = lpgaIdForUpdate;
                    }
                    this.tournamentCourse.courseName = course.name!;
                    this.courseForm.lpgaId = this.normalizeLpgaId(this.course.lpgaId ?? lpgaIdForUpdate);
                    this.originalCourseForm = { ...this.courseForm };
                    this.isCourseChanged = false;
                }
                this.mergeSavedHoles(holes);
                this.mergeHoleYardages(createdYardages);
                this.mergeHoleYardages(savedYardages);
                this.ensureHoleAndYardageCoverage();
                this.changedHoles = [];
                this.changedHoleYardages = [];
                this.isSaving = false;
                this.toastrService.success('Changes saved successfully', 'Success');
            },
            error: (err: any) => {
                this.toastrService.error('Failed to save changes', 'Error');
                this.loggingService.error('Error saving changes:', err);
                this.isSaving = false;
            },
        });
    }

    get canSave(): boolean {
        if (this.isNewCourse) {
            return this.isNewCourseComplete();
        }

        // For existing courses, only enable save if there are changes
        return this.isCourseChanged || this.changedHoles.length > 0 || this.changedHoleYardages.length > 0;
    }

    get hasNoTournamentCourses(): boolean {
        // Filter out the NEW_COURSE_ID placeholder if present
        const actualCourses = this.tournamentCourses.filter(
            (course) => course.courseId !== this.NEW_COURSE_ID
        );
        return actualCourses.length === 0;
    }

    get canImportCourses(): boolean {
        return this.sourceCourseCount() > 0;
    }

    private isNewCourseComplete(): boolean {
        if (!this.courseForm.name?.trim()) {
            return false;
        }

        if (!this.holes.length) {
            return false;
        }

        const allParsDefined = this.holes.every((hole) => {
            const parValue = Number(hole.par);
            return Number.isFinite(parValue) && parValue > 0;
        });

        if (!allParsDefined) {
            return false;
        }

        const primaryRoundId = this.rounds[0]?.roundId;
        if (!primaryRoundId) {
            return false;
        }

        const yardagesForPrimaryRound = this.holeYardages.filter(
            (yardage) => yardage.roundId === primaryRoundId
        );

        if (yardagesForPrimaryRound.length < this.holes.length) {
            return false;
        }

        const yardagesComplete = this.holes.every((hole) => {
            const matching = yardagesForPrimaryRound.find(
                (yardage) => yardage.holeNumber === hole.number
            );

            if (!matching) {
                return false;
            }

            const yardageValue = Number(matching.yardage);
            return Number.isFinite(yardageValue) && yardageValue > 0;
        });

        return yardagesComplete;
    }

    private ensureHoleAndYardageCoverage(): void {
        if (!this.holes.length || !this.rounds.length) {
            return;
        }

        const courseId = this.isNewCourse ? this.NEW_COURSE_ID : this.tournamentCourse.courseId ?? this.NEW_COURSE_ID;
        const existingKeys = new Set<string>();
        this.holeYardages.forEach((yardage) => {
            const key = this.buildYardageKey(yardage.roundId, yardage.holeNumber);
            if (key) {
                existingKeys.add(key);
            }
        });

        const defaultTeeboxId = this.getDefaultTeeboxId();
        const placeholders: HoleYardage[] = [];

        this.rounds.forEach((round) => {
            if (!round.roundId) {
                return;
            }
            this.holes.forEach((hole) => {
                const key = this.buildYardageKey(round.roundId, hole.number);
                if (!key || existingKeys.has(key)) {
                    return;
                }
                placeholders.push({
                    holeYardageId: undefined,
                    holeId: hole.holeId,
                    courseId,
                    roundId: round.roundId,
                    holeNumber: hole.number,
                    yardage: undefined,
                    teeboxId: defaultTeeboxId,
                } as HoleYardage);
                existingKeys.add(key);
            });
        });

        if (placeholders.length) {
            this.holeYardages = [...this.holeYardages, ...placeholders];
        }

        if (defaultTeeboxId) {
            this.applyDefaultTeeboxId(defaultTeeboxId);
        }
    }

    private applyDefaultTeeboxId(teeboxId: string | undefined): void {
        if (!teeboxId) {
            return;
        }

        this.holeYardages.forEach((yardage) => {
            if (!yardage.teeboxId) {
                yardage.teeboxId = teeboxId;
            }
        });
    }

    private getDefaultTeeboxId(): string | undefined {
        const yardageTeebox = this.holeYardages.find((yardage) => !!yardage.teeboxId)?.teeboxId;
        if (yardageTeebox) {
            return yardageTeebox;
        }

        return this.teeBoxes.find((teeBox) => !!teeBox.teeboxId)?.teeboxId;
    }

    private buildYardageKey(roundId: string | undefined, holeNumber: number | undefined): string | undefined {
        if (!roundId || holeNumber === undefined || holeNumber === null) {
            return undefined;
        }

        return `${roundId}-${holeNumber}`;
    }

    private shouldCreateHoleYardage(holeYardage: HoleYardage): boolean {
        if (holeYardage.holeYardageId) {
            return false;
        }

        if (!holeYardage.holeId || !holeYardage.roundId) {
            return false;
        }

        if (!this.isPositiveNumber(holeYardage.yardage)) {
            return false;
        }

        return !this.hasPersistedHoleYardage(holeYardage.holeId, holeYardage.roundId);
    }

    private shouldUpdateHoleYardage(holeYardage: HoleYardage): boolean {
        if (!holeYardage.holeYardageId) {
            return false;
        }

        if (!this.isPositiveNumber(holeYardage.yardage)) {
            return false;
        }

        return true;
    }

    private hasPersistedHoleYardage(holeId: string, roundId: string): boolean {
        return this.holeYardages.some(
            (existing) =>
                !!existing.holeYardageId &&
                existing.holeId === holeId &&
                existing.roundId === roundId
        );
    }

    private isPositiveNumber(value: unknown): boolean {
        const numeric = Number(value);
        return Number.isFinite(numeric) && numeric > 0;
    }

    private normalizeLpgaId(value: unknown): number | undefined {
        const numeric = Number(value);
        return Number.isFinite(numeric) && numeric > 0 ? numeric : undefined;
    }

    private mergeSavedHoles(savedHoles: Hole[]): void {
        if (!savedHoles?.length) {
            return;
        }

        const holesById = new Map<string, Hole>();
        savedHoles.forEach((hole) => {
            if (hole.holeId) {
                holesById.set(hole.holeId, hole);
            }
        });

        if (!holesById.size) {
            return;
        }

        this.holes = this.holes.map((existing) => {
            if (!existing.holeId) {
                return existing;
            }

            const updated = holesById.get(existing.holeId);
            return updated ? { ...existing, ...updated } : existing;
        });
    }

    private mergeHoleYardages(yardages: HoleYardage[]): void {
        if (!yardages?.length) {
            return;
        }

        const updated = [...this.holeYardages];

        yardages.forEach((yardage) => {
            const indexById = yardage.holeYardageId
                ? updated.findIndex((existing) => existing.holeYardageId === yardage.holeYardageId)
                : -1;

            let targetIndex = indexById;

            if (targetIndex === -1) {
                targetIndex = updated.findIndex(
                    (existing) =>
                        existing.holeId === yardage.holeId &&
                        existing.roundId === yardage.roundId &&
                        (existing.holeNumber ?? 0) === (yardage.holeNumber ?? existing.holeNumber ?? 0)
                );
            }

            if (targetIndex !== -1) {
                updated[targetIndex] = { ...updated[targetIndex], ...yardage };
            } else {
                updated.push({ ...yardage });
            }
        });

        this.holeYardages = updated;
    }

    /**
     * Persist a newly configured course, including hole scaffolding and optional yardages, then link it to the tournament.
     */
    private saveNewCourse(): void {
        const name = this.courseForm.name?.trim();
        if (!name) {
            this.toastrService.error('Course Name is required to create a new course.', 'Validation Error');
            return;
        }

        const shortName = (this.courseForm.shortName?.trim() ?? '').toUpperCase();
        this.courseForm.shortName = shortName;
        this.courseForm.lpgaId = this.normalizeLpgaId(this.courseForm.lpgaId);
        const imageUrl = ' ';

        if (!this.holes.length) {
            this.initializeNewCourse();
        }

        this.holes = this.holes.map((hole) => ({
            ...hole,
            image: (hole.image?.trim() || imageUrl) || '',
        }));

        this.isSaving = true;

        const lpgaId = this.resolveCourseLpgaId();
        if (!lpgaId) {
            this.toastrService.error('LPGA ID is required to create a course for this tournament.', 'Validation Error');
            this.isSaving = false;
            return;
        }

        this.courseService.createCourse({ name, shortName, image: imageUrl, tournamentId: this.tournamentId, lpgaId }).pipe(
            switchMap((courseId: string) =>
                this.createDefaultTeebox(courseId).pipe(
                    switchMap((teeboxId: string) => {
                        this.applyDefaultTeeboxId(teeboxId);

                        const holeCreates = this.holes.map((hole) =>
                            this.courseService.createHole(courseId, hole).pipe(
                                map((holeId: string) => ({ number: hole.number ?? 0, holeId }))
                            )
                        );

                        return forkJoin(holeCreates).pipe(
                            switchMap((createdHoles) => {
                                const holeIdMap = new Map<number, string>();
                                createdHoles.forEach(({ number, holeId }) => {
                                    holeIdMap.set(number, holeId);
                                });

                                this.holes = this.holes.map((hole) => ({
                                    ...hole,
                                    courseId,
                                    holeId: holeIdMap.get(hole.number ?? 0) ?? hole.holeId,
                                    image: (hole.image?.trim() || imageUrl) || '',
                                }));

                                this.holeYardages = this.holeYardages.map((yardage) => ({
                                    ...yardage,
                                    courseId,
                                    holeId: holeIdMap.get(yardage.holeNumber ?? 0) ?? yardage.holeId,
                                }));

                                this.applyDefaultTeeboxId(teeboxId);

                                const yardagesToCreate = this.holeYardages.filter(
                                    (yardage) =>
                                        yardage.yardage !== undefined &&
                                        yardage.yardage !== null &&
                                        yardage.holeId
                                );

                                const yardageCreates = yardagesToCreate.map((yardage) =>
                                    this.courseService.createHoleYardage(courseId, yardage.holeId!, yardage)
                                );

                                const yardagePipeline = yardageCreates.length > 0 ? forkJoin(yardageCreates) : of([]);

                                return yardagePipeline.pipe(map(() => courseId));
                            })
                        );
                    })
                )
            )
        ).subscribe({
            next: (courseId: string) => {
                this.toastrService.success('Course created and associated with the tournament.', 'Success');
                this.isSaving = false;
                this.isNewCourse = false;
                this.changedHoles = [];
                this.changedHoleYardages = [];
                this.tournamentCourse = {
                    tournamentId: this.tournamentId,
                    courseId,
                    courseName: name,
                    ...(imageUrl.trim().length > 0 ? { courseImage: imageUrl.trim() } : {}),
                } as TournamentCourse;
                this.course = {
                    courseId,
                    name,
                    shortName,
                    image: imageUrl,
                    lpgaId,
                } as Course;
                this.courseForm.lpgaId = lpgaId;
                this.originalCourseForm = { ...this.courseForm };
                this.loadAllTournamentCourses();
                this.router.navigate(['/tournaments', this.tournamentId, 'courses', courseId]);
            },
            error: (err: any) => {
                this.isSaving = false;
                this.toastrService.error('Failed to create course', 'Error');
                this.loggingService.error('Error creating course:', err);
            },
        });
    }

    private createDefaultTeebox(courseId: string): Observable<string> {
        const teeBoxRequest = {
            name: '',
            color: '',
            slopeRating: 0,
            courseRating: 0,
        };

        return this.courseService.createTeebox(courseId, teeBoxRequest).pipe(
            map((teeboxId: string) => {
                const normalized = teeboxId;
                this.teeBoxes = [{
                    courseId,
                    teeboxId: normalized,
                    name: DEFAULT_TEEBOX_NAME,
                    color: DEFAULT_TEEBOX_COLOR,
                    slopeRating: 0,
                    courseRating: 0,
                } as TeeBox];
                return normalized;
            })
        );
    }
}
